import { centsToDollars, formatDollars } from '@fruitful/fruitils';
import {
    AddIncomeFlow,
    calculateMonthlyTakeHomeCents,
    formatIncomeType,
    formatPayCycle,
    getIncomeOwnerDisplayFormatter,
    type Income,
    type Income as IncomeDto,
    type MembershipIncome,
    type MembershipProfileAnswerWithQuestion,
    type MembershipProfileIncomeAnswer,
    transformEnumToDisplay,
} from '@fruitful/member-profile';
import { useAtomValue } from 'jotai/index';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    CentsToFormattedDollarsRenderer,
    DeleteItemRenderer,
    EditItemRenderer,
} from '../../common/components/GridCellRenderers';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import type {
    MemberDetails,
    MembershipDetails,
} from '../../entities/entityTypes';
import { captureException } from '../../logger/Logger';
import { useDeleteMembershipIncomeAndRefetch } from '../hooks/useDeleteMembershipIncome';
import { useUpsertMembershipIncomeAndRefetch } from '../hooks/useUpsertMembershipIncome';
import { MemberProfileLogger } from '../utils/memberProfileUtils';

export const MembershipIncomeTable = ({
    members,
    incomes,
    incomeProfileAnswer,
}: {
    members: MemberDetails[];
    incomes: MembershipIncome[];
    incomeProfileAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileIncomeAnswer>;
}) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canMutateMembershipIncomes =
        adminUser.authorization.selectedMembership.general.canEdit;

    const membership = useAtomValue(selectedMembershipAtom);
    const { deleteMembershipIncomeAsync } =
        useDeleteMembershipIncomeAndRefetch(membership);

    const [selectedIncome, setSelectedIncome] = useState<IncomeDto | null>(
        null,
    );
    const [isUpsertIncomeModalOpen, setIsUpsertIncomeModalOpen] =
        useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onDelete = async (id: string) => {
        setIsSubmitting(true);

        try {
            await deleteMembershipIncomeAsync(id);
            toast.show('Income deleted!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error deleting income', MutationErrorToastOptions());
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatIncomeOwner = getIncomeOwnerDisplayFormatter(
        members,
        MemberProfileLogger,
    );

    const NoIncome = () => {
        return (
            <BodyMediumRegular fontStyle="italic">
                No Membership Incomes
            </BodyMediumRegular>
        );
    };

    const totalMonthlyTakeHomeCents = calculateMonthlyTakeHomeCents(incomes);
    const monthlyTotal = formatDollars(
        centsToDollars(totalMonthlyTakeHomeCents),
    );

    const profileAnswerMonthlyTotal =
        incomeProfileAnswer?.answer.answer != null &&
        incomeProfileAnswer.answer.answer !== 0
            ? formatDollars(incomeProfileAnswer.answer.answer)
            : null;

    const AddIncomeButton = () => {
        return (
            <XStack
                marginBottom={20}
                width="100%"
                style={{ alignSelf: 'flex-start' }}
                justifyContent={
                    incomes.length > 0 ? 'space-between' : 'flex-end'
                }
                alignItems="center"
            >
                {incomes.length > 0 && (
                    <XStack gap={8} alignItems="center">
                        <BodyMediumEmphasized>
                            Average monthly take-home pay:
                        </BodyMediumEmphasized>
                        <BodyLargeEmphasized>
                            {monthlyTotal}
                        </BodyLargeEmphasized>
                    </XStack>
                )}
                <Button
                    variant="secondary"
                    size="small"
                    onPress={() => {
                        setSelectedIncome(null);
                        setIsUpsertIncomeModalOpen(true);
                    }}
                >
                    Add new income
                </Button>
            </XStack>
        );
    };

    const IncomeTable = () => {
        return (
            <DataTable
                value={incomes}
                size="small"
                tableStyle={{ width: '100%' }}
                scrollable
                scrollHeight="70vh"
            >
                <Column field="id" header="id" hidden={true} />
                <Column
                    field="type"
                    header="type"
                    body={({ type }: MembershipIncome) => {
                        return <>{formatIncomeType(type)}</>;
                    }}
                />
                <Column
                    field="belongsToUserId"
                    header="belongsTo"
                    body={({ belongsToUserId }: MembershipIncome) => {
                        return <>{formatIncomeOwner(belongsToUserId)}</>;
                    }}
                />
                <Column field="description" header="description" />
                <Column
                    field="annualSalaryCents"
                    header="annualSalary"
                    body={({ annualSalaryCents }: MembershipIncome) => {
                        return CentsToFormattedDollarsRenderer(
                            annualSalaryCents,
                        );
                    }}
                />
                <Column
                    field="payPerHourCents"
                    header="payPerHour"
                    body={({ payPerHourCents }: MembershipIncome) => {
                        return CentsToFormattedDollarsRenderer(payPerHourCents);
                    }}
                />
                <Column
                    field="monthlyTakeHomeCents"
                    header="monthlyTakeHome"
                    body={({ monthlyTakeHomeCents }: MembershipIncome) => {
                        return CentsToFormattedDollarsRenderer(
                            monthlyTakeHomeCents,
                        );
                    }}
                />
                <Column
                    field="weeklyTakeHomeCents"
                    header="weeklyTakeHome"
                    body={({ weeklyTakeHomeCents }: MembershipIncome) => {
                        return CentsToFormattedDollarsRenderer(
                            weeklyTakeHomeCents,
                        );
                    }}
                />
                <Column field="weeklyHours" header="weeklyHours" />
                <Column
                    field="payCycle"
                    header="payCycle"
                    body={({ payCycle }: MembershipIncome) => {
                        return (
                            <>
                                {payCycle != null
                                    ? formatPayCycle(payCycle)
                                    : ''}
                            </>
                        );
                    }}
                />
                <Column
                    field="additionalCompensation"
                    header="additionalCompensation"
                    body={({ additionalCompensation }: MembershipIncome) => {
                        return (
                            <>
                                {additionalCompensation != null
                                    ? additionalCompensation
                                          .map(transformEnumToDisplay)
                                          .join(', ')
                                    : ''}
                            </>
                        );
                    }}
                />
                <Column
                    field="additionalInformation"
                    header="additionalInformation"
                />
                <Column
                    header=""
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={(income: MembershipIncome) => {
                        return (
                            <EditItemRenderer
                                onPress={() => {
                                    setSelectedIncome(income);
                                    setIsUpsertIncomeModalOpen(true);
                                }}
                            />
                        );
                    }}
                    hidden={!canMutateMembershipIncomes}
                />
                <Column
                    header=""
                    style={{ width: '3rem', paddingLeft: 0 }}
                    body={({ id }: MembershipIncome) => {
                        return (
                            <DeleteItemRenderer
                                onDelete={async () => {
                                    await onDelete(id);
                                }}
                            />
                        );
                    }}
                    hidden={!canMutateMembershipIncomes}
                />
            </DataTable>
        );
    };

    return (
        <YStack
            borderRadius="$4"
            backgroundColor="$white"
            padding="$5"
            gap="$4"
            shadowColor="$red800"
            shadowOffset={{ width: 0, height: 1 }}
            shadowOpacity={0.5}
            shadowRadius={4}
        >
            <YStack
                borderBottomWidth={1}
                borderColor="$gray200"
                paddingBottom="$2"
                marginBottom="$4"
            >
                <BodyLargeEmphasized>Incomes</BodyLargeEmphasized>
            </YStack>
            {profileAnswerMonthlyTotal != null && (
                <XStack gap={8} alignItems="center" marginBottom="$4">
                    <BodyMediumEmphasized>
                        Average monthly income (3 Magic Numbers):
                    </BodyMediumEmphasized>
                    <BodyLargeEmphasized>
                        {profileAnswerMonthlyTotal}
                    </BodyLargeEmphasized>
                </XStack>
            )}
            <YStack gap={12}>
                {isSubmitting ? (
                    <Spinner />
                ) : !incomes.length ? (
                    <YStack>
                        <NoIncome />
                        <AddIncomeButton />
                    </YStack>
                ) : (
                    <YStack>
                        <AddIncomeButton />
                        <IncomeTable />
                    </YStack>
                )}
                <UpsertIncomeModal
                    membership={membership}
                    selectedIncome={selectedIncome}
                    setSelectedIncome={setSelectedIncome}
                    isUpsertIncomeModalOpen={isUpsertIncomeModalOpen}
                    setIsUpsertIncomeModalOpen={setIsUpsertIncomeModalOpen}
                    setIsSubmitting={setIsSubmitting}
                />
            </YStack>
        </YStack>
    );
};

interface UpsertIncomeModalProps {
    membership: MembershipDetails | null;
    selectedIncome: Income | null;
    setSelectedIncome: (income: Income | null) => void;
    isUpsertIncomeModalOpen: boolean;
    setIsUpsertIncomeModalOpen: (isOpen: boolean) => void;
    setIsSubmitting: (isSubmitting: boolean) => void;
}

const UpsertIncomeModal = ({
    membership,
    selectedIncome,
    setSelectedIncome,
    isUpsertIncomeModalOpen,
    setIsUpsertIncomeModalOpen,
    setIsSubmitting,
}: UpsertIncomeModalProps) => {
    const toast = useToastController();

    const { upsertMembershipIncomeAsync } =
        useUpsertMembershipIncomeAndRefetch(membership);

    if (membership == null) {
        captureException(new Error('Cannot determine membership.'));
        return <></>;
    }

    const onComplete = async (data: IncomeDto) => {
        setIsSubmitting(true);

        try {
            const upsertDto = {
                ...data,
                membershipId: membership.id,
            };

            await upsertMembershipIncomeAsync(membership.id, [upsertDto]);

            toast.show('Income upserted!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error upserting income', MutationErrorToastOptions());
        } finally {
            setIsUpsertIncomeModalOpen(false);
            setSelectedIncome(null);
            setIsSubmitting(false);
        }
    };

    const onExit = () => {
        setIsUpsertIncomeModalOpen(false);
        setSelectedIncome(null);
    };

    const onError = (exception: Error) => {
        captureException(exception);
        toast.show('Something went wrong!', { duration: 2000 });
    };

    return (
        <GroveSimpleModal
            isOpen={isUpsertIncomeModalOpen}
            onClose={() => {
                setIsUpsertIncomeModalOpen(false);
            }}
        >
            <AddIncomeFlow
                existingData={selectedIncome ?? {}}
                membership={membership}
                onComplete={onComplete}
                onExit={onExit}
                onError={onError}
                logger={MemberProfileLogger}
            />
        </GroveSimpleModal>
    );
};
